import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Disc3, Images, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useTeam } from "@/lib/team";
import { api } from "@/lib/api";
import { DueTag } from "@/lib/due";

function Recap({ y }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="welcome-recap">
      {[["Listed yesterday", y.listed, "listed"], ["Drafted yesterday", y.drafted, "drafted"]].map(([label, n, id]) => (
        <div key={id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="font-mono text-2xl font-bold text-slate-900" data-testid={`welcome-recap-${id}`}>{n || 0}</div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
        </div>
      ))}
    </div>
  );
}

const seenKey = (email) => `snaplist.asstWelcome.${email}`;
const today = () => new Date().toLocaleDateString("en-CA");

function Row({ b, icon: Icon, onOpen }) {
  return (
    <button type="button" onClick={onOpen} data-testid={`welcome-assignment-${b.id}`}
      className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:bg-slate-50">
      <Icon className="h-4 w-4 shrink-0 text-[#0053DA]" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-900">{b.name}</span>
        <span className="block text-xs text-slate-500">{b.left ? `${b.left} of ${b.total} items to go` : `${b.total} items · all done`}</span>
      </span>
      <DueTag due={b.left ? b.due_date : ""} testId={`welcome-due-${b.id}`} />
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
    </button>
  );
}

export default function AssistantWelcome() {
  const { user } = useAuth();
  const { isAssistant, ownerName } = useTeam();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const email = (user?.email || "").toLowerCase();

  useEffect(() => {
    if (!isAssistant || !email || localStorage.getItem(seenKey(email)) === today()) return;
    const show = (d) => {
      // wait for any other pop-up (install guide, tips) to close so they never stack
      if (!document.querySelector('[role="dialog"]')) { setData(d); return; }
      const obs = new MutationObserver(() => { if (!document.querySelector('[role="dialog"]')) { obs.disconnect(); setData(d); } });
      obs.observe(document.body, { childList: true, subtree: true });
    };
    const load = () => api.get("/my-assignments", { params: { tz_offset: new Date().getTimezoneOffset() } }).then((r) => setTimeout(() => show(r.data), 800)).catch(() => {});
    if (localStorage.getItem(`snaplist.vaTour.${email}`)) { load(); return; }
    window.addEventListener("snaplist:va-tour-done", load, { once: true }); // wait for the first-visit tour
    return () => window.removeEventListener("snaplist:va-tour-done", load);
  }, [isAssistant, email]);

  if (!data) return null;
  const close = () => { localStorage.setItem(seenKey(email), today()); setData(null); };
  const first = (user?.name || email.split("@")[0]).split(" ")[0];
  const items = [...data.photo.map((b) => ({ ...b, kind: "photo" })), ...data.media.map((b) => ({ ...b, kind: "media" }))].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
  const open = (b) => {
    close();
    if (b.kind === "media") { localStorage.setItem("snaplist.mediaBatch", b.id); nav("/media"); } else nav(`/batch?b=${b.id}`);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="assistant-welcome">
        <DialogHeader>
          <DialogTitle className="text-xl" data-testid="assistant-welcome-title">Hi {first}, welcome back!</DialogTitle>
          <DialogDescription>
            {items.length ? `Here's what ${ownerName || "the owner"} assigned to you today.` : `Nothing is assigned to you yet. ${ownerName || "The owner"} will send you a batch soon.`}
          </DialogDescription>
        </DialogHeader>
        <Recap y={data.yesterday || {}} />
        {!!items.length && (
          <div className="max-h-72 space-y-2 overflow-y-auto" data-testid="assistant-welcome-list">
            {items.map((b) => <Row key={b.id} b={b} icon={b.kind === "media" ? Disc3 : Images} onOpen={() => open(b)} />)}
          </div>
        )}
        <p className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Other batches stay locked. Tap a lock to ask for access and the owner can approve it.
        </p>
        <Button onClick={close} className="w-full bg-[#0053DA] hover:bg-[#0042ad]" data-testid="assistant-welcome-close">Let's go</Button>
      </DialogContent>
    </Dialog>
  );
}
